import type { Au06Output } from "./types.js";

// Plain-English result copy for AU-06 (HECS/HELP repayment). Lives in the rules
// package — not in any single app — so every surface that explains an AU-06
// result (the Expo result screen, the "my results" list, the PDF report)
// renders identical wording from one source. That matters for regulation, not
// just DRY: every string here is information, not advice (validation §3.1) — we
// state what the compulsory repayment IS and how it's derived from the ATO
// marginal bands, with no "you should pay more", no "switch", no product
// recommendation, no "save $X". Drift between channels is the
// information → advice kill-threat, so the copy is centralised here alongside
// the rules it describes. Pure string-builders, unit-tested with the engine.

const FY_LABEL = "FY26";

/** Whole-dollar AUD with thousands separators. Kept local to the copy layer so
 * the wording renders the same numbers everywhere, independent of any app's UI
 * formatter. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Repayment rates are whole-cent multiples (15c, 17c, 10c per $1). */
function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

export function repaymentHeadline(out: Au06Output): string {
  return out.compulsoryRepayment === 0
    ? "A$0 — no compulsory study loan repayment at this income"
    : `Your compulsory study loan repayment for ${FY_LABEL}: ${formatAud(out.compulsoryRepayment)}`;
}

/** States the annual repayment as a figure — never as a saving or target. */
export function annualRepayment(out: Au06Output): string {
  return out.compulsoryRepayment === 0
    ? "A$0 in compulsory study loan repayment this year"
    : `${formatAud(out.compulsoryRepayment)} in compulsory study loan repayment this year`;
}

/** How the repayment was derived from the marginal bands. */
export function repaymentBullet(out: Au06Output): string {
  if (!out.repaymentDue) {
    return `Your repayment income of ${formatAud(out.repaymentIncome)} is below the ${FY_LABEL} minimum repayment threshold, so no compulsory study loan repayment applies.`;
  }
  if (out.nextBand === null) {
    // Top band: 10% of whole repayment income, not marginal.
    return `Your repayment income of ${formatAud(out.repaymentIncome)} falls in the top ${FY_LABEL} repayment band (${out.thresholdBand}). In this band the compulsory repayment is ${ratePct(out.marginalRate)} of your whole repayment income, which is ${formatAud(out.compulsoryRepayment)}.`;
  }
  return `Your repayment income of ${formatAud(out.repaymentIncome)} falls in the ${out.thresholdBand} band for ${FY_LABEL}. Under the marginal system the compulsory repayment is charged only on the income above each band's threshold, which works out to ${formatAud(out.compulsoryRepayment)}.`;
}

/** Where the next band starts and how far away it is — descriptive, not a nudge. */
export function nextBandBullet(out: Au06Output): string | null {
  if (out.nextBand === null) return null;
  return `The next repayment band starts at a repayment income of ${formatAud(out.nextBand.at)} — that is ${formatAud(out.nextBand.away)} above your current repayment income.`;
}

/** Source-attribution line, composed from the ruleset metadata the server
 * returns so it always names the rules actually in force. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return `These figures use the ATO study and training loan repayment thresholds for ${FY_LABEL} (ruleset ${ruleVersion}), last verified ${verifiedAt}. Thresholds and rates are indexed each financial year — always check ato.gov.au.`;
}

export function explanationBullets(
  out: Au06Output,
  ruleVersion: string,
  verifiedAt: string,
): string[] {
  const next = nextBandBullet(out);
  return [
    repaymentBullet(out),
    ...(next ? [next] : []),
    sourceBullet(ruleVersion, verifiedAt),
  ];
}
